import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AgentDocument, GeneratedLink } from './types';

export type AgentDocsState = {
  documents: AgentDocument[];
  reportLinksByDocId: Record<string, GeneratedLink[]>;
  responseMessageByDocId: Record<string, string>;
  isLoadingDocuments: boolean;
  generatingByDocId: Record<string, boolean>;
  docsMessage: string;
};

const initialState: AgentDocsState = {
  documents: [],
  reportLinksByDocId: {},
  responseMessageByDocId: {},
  isLoadingDocuments: false,
  generatingByDocId: {},
  docsMessage: '',
};

const agentDocsSlice = createSlice({
  name: 'agentDocs',
  initialState,
  reducers: {
    setAgentDocsLoading(state, action: PayloadAction<boolean>) {
      state.isLoadingDocuments = action.payload;
    },
    setAgentDocsData(
      state,
      action: PayloadAction<{
        documents: AgentDocument[];
        docsMessage: string;
      }>,
    ) {
      state.documents = action.payload.documents;
      state.docsMessage = action.payload.docsMessage;
    },
    setAgentDocsMessage(state, action: PayloadAction<string>) {
      state.docsMessage = action.payload;
    },
    setAgentDocGenerating(
      state,
      action: PayloadAction<{ docId: string; loading: boolean }>,
    ) {
      const { docId, loading } = action.payload;
      state.generatingByDocId[docId] = loading;
    },
    setAgentDocLinks(
      state,
      action: PayloadAction<{ docId: string; links: GeneratedLink[] }>,
    ) {
      const { docId, links } = action.payload;
      state.reportLinksByDocId[docId] = links;
    },
    setAgentDocResponseMessage(
      state,
      action: PayloadAction<{ docId: string; message: string }>,
    ) {
      const { docId, message } = action.payload;
      state.responseMessageByDocId[docId] = message;
    },
    clearAgentDocsResponseMessage(
      state,
      action: PayloadAction<{ docId: string }>,
    ) {
      state.responseMessageByDocId[action.payload.docId] = '';
    },
    resetAgentDocs(state) {
      state.documents = [];
      state.reportLinksByDocId = {};
      state.responseMessageByDocId = {};
      state.isLoadingDocuments = false;
      state.generatingByDocId = {};
      state.docsMessage = '';
    },
  },
});

export const {
  setAgentDocsLoading,
  setAgentDocsData,
  setAgentDocsMessage,
  setAgentDocGenerating,
  setAgentDocLinks,
  setAgentDocResponseMessage,
  clearAgentDocsResponseMessage,
  resetAgentDocs,
} = agentDocsSlice.actions;

const reducer = {
  agentDocs: agentDocsSlice.reducer,
};

export default reducer;
